import React, { useState, useEffect } from 'react'
import { useToast } from '~/lib/ui/toast'
import { apiPost, apiGet } from '~/util/api'
import { Textarea } from '~/lib/ui/textarea'
import { Label } from '~/lib/ui/label'
import { Button } from '~/components/_atoms'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from '~/lib/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/lib/ui/dialog'
import { Checkbox } from '~/lib/ui/checkbox'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/lib/ui/accordion'
import PrivacySettings from '~/components/CoachCandidateView/PrivacySettings'
import { useTranslation } from '~/hooks/useTranslation'
import { useCoachChat } from './CoachChatContext'

const DurationSelect = ({ onValueChange, duration, getDarkModeClasses }) => {
  const durationOptions = [1, 2, 5, 10, 15, 30, 45, 60, 75, 90, 'Custom']
  const [hasCustomDuration, setHasCustomDuration] = useState(
    duration !== '' && !durationOptions.includes(duration),
  )

  const handleValueChange = (value) => {
    if (value === 'Custom') {
      setHasCustomDuration(true)
    } else {
      setHasCustomDuration(false)
      onValueChange(value)
    }
  }

  return (
    <div className="flex flex-row justify-evenly">
      <Select
        value={hasCustomDuration ? 'Custom' : duration.toString()}
        onValueChange={handleValueChange}
        className="w-full"
      >
        <SelectTrigger>
          <SelectValue placeholder="Select Duration" />
        </SelectTrigger>
        <SelectContent>
          {durationOptions.map((option) => (
            <SelectItem key={option} value={option.toString()}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasCustomDuration && (
        <input
          type="number"
          className={`ml-2 w-full rounded border px-3 py-2 ${getDarkModeClasses(
            'border-gray-300 bg-white text-gray-900',
            'border-gray-600 bg-gray-700 text-white',
          )}`}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Custom Duration"
          value={duration}
        />
      )}
    </div>
  )
}

const AddCoachNoteModal = ({ chat, onClose, onSuccess }) => {
  const { t } = useTranslation('coach_chat')
  const { getDarkModeClasses } = useCoachChat()
  const [noteDetails, setNoteDetails] = useState('')
  const [privacySettings, setPrivacySettings] = useState([])
  const [selectedTopics, setSelectedTopics] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [duration, setDuration] = useState(
    chat.duration_minutes?.toString() || '',
  )
  const [coachNoteTopics, setCoachNoteTopics] = useState([])
  const { toast } = useToast()

  // Fetch coach note topics
  useEffect(() => {
    if (!chat.candidate_id) return

    const fetchTopics = async () => {
      try {
        const response = await apiGet(
          `/coach/dashboard_candidates/${chat.candidate_id}/coach_notes.json`,
        )
        if (response.status === 200) {
          // Filter out "Outreach" topics for coach chat (matching old behavior)
          const topics =
            response.data?.candidateCoachNotes?.coachNoteTopics?.filter(
              (topicGroup) => topicGroup.category !== 'Outreach',
            ) || []
          setCoachNoteTopics(topics)
        }
      } catch (err) {
        console.error('Error fetching coach note topics:', err)
      }
    }

    fetchTopics()
  }, [chat.candidate_id])

  const saveClick = async () => {
    if (isSaving) return null

    const f = new FormData()
    f.append('coach_note[duration]', duration)
    f.append('coach_note[note]', noteDetails)
    f.append('coach_note[privacy_settings][]', 'self')
    const allPrivacySettings = ['csm', 'coach', 'admin']
    const formPrivacySettings =
      privacySettings?.indexOf('all') > -1
        ? allPrivacySettings
        : privacySettings
    formPrivacySettings.forEach((setting) => {
      f.append('coach_note[privacy_settings][]', setting)
    })

    // Add topics
    Object.entries(selectedTopics).forEach(([category, topics]) => {
      topics.forEach((topic) => {
        f.append(`coach_note[topics][${category}][]`, topic)
      })
    })

    // Add chat_history_id if available
    if (chat.id) {
      f.append('coach_note[chat_history_id]', chat.id)
    }

    setIsSaving(true)
    try {
      const postPath = `/coach/candidates/${chat.candidate_id}/coach_notes.json`
      const response = await apiPost(postPath, f, true)

      if (response.status === 200 || response.status === 201) {
        toast({
          title: t('coach_note.success_title', 'Success'),
          description: t(
            'coach_note.success_description',
            'Coach Note created successfully.',
          ),
        })
        onSuccess()
      } else {
        throw new Error('Failed to create coach note')
      }
    } catch (error) {
      console.error('Error creating coach note:', error)
      const description =
        error?.response?.data?.error ||
        t(
          'coach_note.error_description',
          'An error occurred while creating the coach note.',
        )
      toast({
        description,
        title: t('coach_note.error_title', 'Could Not Create Coach Note'),
        variant: 'destructive',
        duration: 10_000,
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className={`flex max-h-[80vh] flex-col gap-3 overflow-hidden rounded-2xl font-roboto lg:max-w-3xl xl:max-w-5xl ${getDarkModeClasses(
          'bg-white',
          'bg-gray-800',
        )}`}
        variant="default"
      >
        <DialogHeader className="flex-shrink-0 py-3">
          <DialogTitle className="text-left" asChild>
            <h2
              className={`text-2xl ${getDarkModeClasses('text-white', 'text-white')}`}
            >
              {t('coach_note.add_note_title', 'Add Coach Note')}
            </h2>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription
          className={`flex-shrink-0 py-0 ${getDarkModeClasses(
            'text-gray-600',
            'text-gray-300',
          )}`}
        >
          {t('coach_note.add_note_description', {
            default: 'Add a coach note for chat with %{candidate_name}',
            candidate_name: chat.candidate_name || 'candidate',
          })}
        </DialogDescription>
        <div
          className={`min-h-0 flex-1 overflow-y-auto px-4 pb-3 ${getDarkModeClasses(
            'bg-white',
            'bg-gray-800',
          )}`}
        >
          <div className="pb-3">
            <Label
              htmlFor="duration"
              className={`text-lg !font-medium ${getDarkModeClasses(
                'text-gray-900',
                'text-gray-200',
              )}`}
            >
              {t('coach_note.duration_label', 'Duration (Minutes)')}
            </Label>
            <DurationSelect
              onValueChange={setDuration}
              duration={duration}
              getDarkModeClasses={getDarkModeClasses}
            />
          </div>
          <div className="pb-3">
            <Label
              htmlFor="topics"
              className={`text-lg !font-medium ${getDarkModeClasses(
                'text-gray-900',
                'text-gray-200',
              )}`}
            >
              {t('coach_note.topics_label', 'Topics')}*
            </Label>
            <Topics
              selectedTopics={selectedTopics}
              setSelectedTopics={setSelectedTopics}
              coachNoteTopics={coachNoteTopics}
              getDarkModeClasses={getDarkModeClasses}
            />
          </div>
          <div className="pb-3">
            <Label
              htmlFor="noteDetails"
              className={`text-lg !font-medium ${getDarkModeClasses(
                'text-gray-900',
                'text-gray-200',
              )}`}
            >
              {t('coach_note.note_details_label', 'Note Details')}
            </Label>
            <Textarea
              className={`w-full ${getDarkModeClasses(
                'border-gray-300 bg-white text-gray-900',
                'border-gray-600 bg-gray-700 text-white',
              )}`}
              name="noteDetails"
              value={noteDetails}
              onChange={(e) => setNoteDetails(e.target.value)}
              rows={4}
            />
          </div>
          <div className="pb-3">
            <PrivacySettings
              setPrivacySettings={setPrivacySettings}
              privacySettings={privacySettings}
              getDarkModeClasses={getDarkModeClasses}
            />
          </div>
          <div className="flex flex-row justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              {t('coach_note.cancel', 'Cancel')}
            </Button>
            <Button onClick={saveClick} disabled={isSaving}>
              {t('coach_note.save', 'Save')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AddCoachNoteModal

const Topics = ({
  selectedTopics,
  setSelectedTopics,
  coachNoteTopics,
  getDarkModeClasses,
}) => {
  return (
    <div className="grid grid-cols-2 gap-5">
      {coachNoteTopics?.map((topicGroup, idx) => (
        <TopicGroup
          key={topicGroup.category}
          topicGroup={topicGroup}
          setSelectedTopics={setSelectedTopics}
          selectedTopics={selectedTopics}
          idx={idx}
          getDarkModeClasses={getDarkModeClasses}
        />
      ))}
    </div>
  )
}

const TopicGroup = ({
  topicGroup,
  selectedTopics,
  setSelectedTopics,
  idx,
  getDarkModeClasses,
}) => {
  const { category, topics } = topicGroup

  const expanded =
    selectedTopics[category] && selectedTopics[category].length > 0

  const expandedProps = expanded ? { defaultValue: `item-${idx}` } : {}

  return (
    <div>
      <Accordion type="single" collapsible {...expandedProps}>
        <AccordionItem value={`item-${idx}`}>
          <AccordionTrigger
            className={`py-3 ${getDarkModeClasses('text-gray-900', 'text-gray-200')}`}
          >
            <h3>{category}</h3>
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-2">
              {topics
                .filter((topic) => topic?.topic) // Filter out null/undefined topics
                .map((topic, topicIdx) => {
                  return (
                    <TopicCheckbox
                      key={`${topic.topic}-${topicIdx}`}
                      selectedTopics={selectedTopics}
                      setSelectedTopics={setSelectedTopics}
                      topic={topic}
                      category={category}
                      getDarkModeClasses={getDarkModeClasses}
                    />
                  )
                })}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

const TopicCheckbox = ({
  topic,
  category,
  selectedTopics,
  setSelectedTopics,
  getDarkModeClasses,
}) => {
  const checked = selectedTopics[category]?.includes(topic.topic)

  const handleCheckboxChange = () => {
    const newTopics = { ...selectedTopics }
    if (!newTopics[category]) {
      newTopics[category] = []
    }
    if (checked) {
      newTopics[category] = newTopics[category].filter((t) => t !== topic.topic)
    } else {
      newTopics[category].push(topic.topic)
    }
    setSelectedTopics(newTopics)
  }

  return (
    <div className="flex items-center gap-2">
      <Checkbox
        onCheckedChange={handleCheckboxChange}
        checked={checked}
        id={topic.topic}
      />
      <Label
        htmlFor={topic.topic}
        className={getDarkModeClasses('text-gray-900', 'text-gray-200')}
      >
        {topic?.topic}
      </Label>
    </div>
  )
}
